// Debug helpers for per-rule source metadata and CSS annotations.
// Used by incremental compile path when `debug` option is enabled.
import type { LuisRenderLeaf } from '../render'
import type { LuisCompileDebugRule } from '../types'

// Build one rendered rule with an optional source marker in debug mode.
export function wrapRuleWithDebugSourceComment(
  rule: string,
  sourceId: string,
  sourceLine: number,
  lineOffset: number,
  debugEnabled: boolean,
): string {
  // Check whether debug markers are enabled for the current rule.
  if (!debugEnabled) {
    // Return the rendered rule unchanged when debug markers are disabled.
    return rule
  } else {
    // Continue source marker emission for debug mode.
  }

  const line = normalizeDebugLine(sourceLine, lineOffset)
  // Return CSS rule prefixed with inspect-friendly source marker.
  return `/* luis-source: ${sourceId}:${line} */\n${rule}`
}

// Build per-rule debug metadata only when debug mode is enabled.
export function collectDebugRules(
  leaves: LuisRenderLeaf[],
  sourceId: string,
  lineOffset: number,
  debugEnabled: boolean,
): LuisCompileDebugRule[] | undefined {
  // Check whether debug rule metadata is enabled for the current compile.
  if (!debugEnabled) {
    // Return undefined when debug rule metadata is disabled.
    return undefined
  } else {
    // Continue debug rule-map collection.
  }

  // Return selector rule map for runtime inspect helpers.
  return leaves.map((leaf) => ({
    key: leaf.key,
    selector: leaf.selector,
    atRules: [...leaf.atRules],
    line: normalizeDebugLine(leaf.sourceLine, lineOffset),
    sourceId,
  }))
}

// Resolve parser lines back to source lines by subtracting the prelude offset.
function normalizeDebugLine(sourceLine: number, lineOffset: number): number {
  const normalized = sourceLine - lineOffset
  // Clamp line number to a valid source line lower bound.
  return normalized > 0 ? normalized : 1
}
